"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, CalendarCheck, ShieldCheck, Sparkles, ArrowRight, PhoneCall, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
interface StepItem {
  id: string;
  stepNumber: string;
  title: string;
  description: string;
  iconName: "FileText" | "CalendarCheck" | "ShieldCheck" | "Sparkles";
  detailHighlight: string;
}
const stepsData: StepItem[] = [{
  id: "step-1",
  stepNumber: "01",
  title: "Initial inquiry and free estimate",
  description: "Tell us about your project or send over a few photos of your property. We provide a clear, itemised price breakdown without hidden extras.",
  iconName: "FileText",
  detailHighlight: "Clear, itemised no-obligation quote"
}, {
  id: "step-2",
  stepNumber: "02",
  title: "Site survey and scheduling",
  description: "We check access points, ground levels, and protection requirements, then confirm an exact installation date that suits your schedule.",
  iconName: "CalendarCheck",
  detailHighlight: "Scheduled installation date confirmed with you"
}, {
  id: "step-3",
  stepNumber: "03",
  title: "Safe, tidy erection with protection",
  description: "Our accredited team arrives in uniform, lays protective baseplates for your driveway or lawn, and erects your scaffolding swiftly and quietly.",
  iconName: "ShieldCheck",
  detailHighlight: "Driveway and garden protection included"
}, {
  id: "step-4",
  stepNumber: "04",
  title: "Prompt dismantling and cleanup",
  description: "Once your work is complete, let us know. We take down the structure on time, pack up all equipment, and clear away all equipment and tidy the work area.",
  iconName: "Sparkles",
  detailHighlight: "Site cleared on completion"
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: "easeOut"
    }
  }
};
export default function Expectations() {
  const shouldReduceMotion = useReducedMotion();
  const getStepIcon = (iconName: StepItem["iconName"]) => {
    switch (iconName) {
      case "FileText":
        return <FileText className="size-5 text-primary" />;
      case "CalendarCheck":
        return <CalendarCheck className="size-5 text-primary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-5 text-primary" />;
      case "Sparkles":
        return <Sparkles className="size-5 text-primary" />;
      default:
        return <ShieldCheck className="size-5 text-primary" />;
    }
  };
  return <section id="what-to-expect" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-muted border border-border text-muted-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="size-1.5 rounded-full bg-primary" />
            Homeowner guide
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            What to expect when you book scaffolding
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            A tidy, reliable four-stage process engineered around your home,
            keeping disruption to an absolute minimum from first contact to final
            cleanup.
          </p>
        </div>

        {/* Steps Grid */}
        <motion.div variants={containerVariants} initial={shouldReduceMotion ? "show" : "hidden"} whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stepsData.map((step, index) => <motion.div key={step.id} data-index={index} variants={itemVariants} className="flex flex-col h-full">
              <Card className="h-full border border-border bg-card rounded-lg shadow-sm hover:shadow-md hover:border-primary/40 transition-[box-shadow,border-color] duration-200 flex flex-col justify-between overflow-hidden">
                <CardHeader className="space-y-4 pb-2">
                  <div className="flex items-center justify-between">
                    <div className="size-10 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center">
                      {getStepIcon(step.iconName)}
                    </div>
                    <span className="font-mono text-2xl font-bold text-muted-foreground/60">
                      {step.stepNumber}
                    </span>
                  </div>
                  <CardTitle className="text-xl font-bold text-card-foreground leading-snug">
                    {step.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-2 flex-1 flex flex-col justify-between">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                  <div className="pt-3 border-t border-border flex items-center gap-2 text-xs font-medium text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>{step.detailHighlight}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Context Banner & CTAs */}
        <div className="mt-12 md:mt-16 rounded-lg bg-secondary text-secondary-foreground p-6 sm:p-8 border border-border/30 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 shadow-sm">
          <div className="space-y-1 max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-bold text-secondary-foreground">
              Ready to schedule your scaffolding?
            </h3>
            <p className="text-sm text-secondary-foreground/80 leading-relaxed">
              We cover Berkshire, Surrey, Hampshire, Buckinghamshire, London, and
              the wider South East with full NASC and CHAS compliance.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <Button asChild className="w-full sm:w-auto font-semibold">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto bg-transparent border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground font-semibold">
              <Link to="/ContactRequestQuotePage">
                <PhoneCall className="size-4 mr-1" />
                Discuss your project
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}